import React from 'react';
import { Draggable, Droppable } from 'react-drag-and-drop';
import { CSSTransitionGroup } from 'react-css-transition';
import { get, set, findIndex, uniqBy } from 'lodash';
import pt from 'prop-types';

import { classNames } from 'utils';
import { FadeIn } from 'components/transitions';

import Label from './label';
import FileReader from './file-reader';
import FileDropzone from './file-dropzone';

const droppableTypes = Object.freeze(['file']);

export default class InputFile extends React.PureComponent {
  static contextTypes = {
    openModal: pt.func.isRequired,
    uploadFiles: pt.func.isRequired,
  };

  static propTypes = {
    disabled: pt.bool,
    multiple: pt.bool,
    minimal: pt.bool,
    value: pt.any,
    defaultValue: pt.any,
    valuePath: pt.string,
    accept: pt.oneOfType([pt.string, pt.array]),
    label: pt.string,
    typeLabel: pt.string,
    readonlyContent: pt.bool,
    readonly: pt.bool,
    sizeType: pt.string,
    fullSize: pt.bool,
    maxWidth: pt.number,
    maxHeight: pt.number,
    minWidth: pt.number,
    minHeight: pt.number,
    help: pt.oneOfType([pt.node, pt.object]),
    wide: pt.bool,
    onChange: pt.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      dragSourceId: '',
      removedFileIds: new Set(),
      sortOrder: [],
      uploading: false,
      uploadedFiles: [],
    };

    /** @type {React.RefObject<HTMLInputElement | null>} */
    this.inputRef = React.createRef();
  }

  fileId(val) {
    const { valuePath } = this.props;
    return valuePath ? get(val, valuePath, {}).id : val?.id;
  }

  setValue(uploadedFiles = []) {
    const { multiple, value, defaultValue, valuePath } = this.props;
    const { sortOrder, removedFileIds } = this.state;

    let newValue = [
      ...(multiple ? value || defaultValue : []),
      ...uploadedFiles,
    ].reduce((acc, fileVal) => {
      if (fileVal) {
        const file = (valuePath ? get(fileVal, valuePath) : fileVal) || {};

        if (!removedFileIds.has(this.fileId(file))) {
          const nextVal = { ...fileVal };

          const nextFileVal = {
            id: file.id,
            date_uploaded: file.date_uploaded,
            length: file.length,
            md5: file.md5,
            filename: file.filename,
            content_type: file.content_type,
            metadata: file.metadata,
            url: file.url,
            uploaded_url: file.uploaded_url,
          };

          if (valuePath) {
            set(nextVal, valuePath, nextFileVal);
          } else {
            Object.assign(nextVal, nextFileVal);
          }

          acc.push(nextVal);
        }
      }

      return acc;
    }, []);

    if (!multiple) {
      return newValue[0];
    }

    newValue = uniqBy(newValue, (file) => this.fileId(file));

    if (sortOrder.length > 0) {
      const orderMap = sortOrder.reduce(
        (map, id, i) => map.set(id, i),
        new Map(),
      );

      newValue.sort((a, b) => {
        const indexA = orderMap.get(this.fileId(a));
        const indexB = orderMap.get(this.fileId(b));

        if (indexA === indexB) return 0;
        if (indexA === undefined || indexA > indexB) return 1;
        return -1;
      });

      return newValue;
    }

    return newValue;
  }

  triggerChange(uploadedFiles) {
    const event = new Event('change', { bubbles: true });
    this.inputRef.current.dispatchEvent(event);
    this.props.onChange(event, this.setValue(uploadedFiles));
  }

  onUploadFiles = (files) => {
    const { valuePath, sizeType } = this.props;
    const { uploadFiles } = this.context;

    this.setState({ uploading: true });

    uploadFiles(files, sizeType)
      .then((results) => {
        if (!results) {
          this.setState({ uploading: false });
          return;
        }

        const uploadedFiles = results.reduce((acc, result) => {
          if (result && result.id) {
            const value = {
              id: result.id,
              date_uploaded: result.date_uploaded,
              length: result.length,
              md5: result.md5,
              filename: result.filename,
              content_type: result.content_type,
              metadata: result.metadata,
              url: result.url,
              uploaded_url: result.uploaded_url,
              // Used for processing file contents locally (e.g. zip files)
              instance: result.instance,
            };

            let file = {};

            if (valuePath) {
              set(file, valuePath, value);
            } else {
              Object.assign(file, value);
            }

            acc.push(file);
          }

          return acc;
        }, []);

        this.setState(
          (state, props) => {
            if (!props.multiple) {
              state.uploadedFiles.length = 0;
            }

            state.uploadedFiles.push(...uploadedFiles);

            return {
              uploadedFiles: state.uploadedFiles,
              uploading: false,
            };
          },
          () => this.triggerChange(this.state.uploadedFiles),
        );
      })
      .catch(() => {
        this.setState({ uploading: false });
      });
  };

  onClickRemove = (event) => {
    event.preventDefault();
    const fileId = event.currentTarget.dataset.fileid;
    this.context.openModal('Confirm', {
      title: 'Remove file',
      message: <p>Are you sure you want to remove this file?</p>,
      action: 'Remove',
      actionType: 'danger',
      onConfirm: () => {
        this.setState(
          ({ removedFileIds }) => {
            removedFileIds.add(fileId);
            return { removedFileIds };
          },
          () => this.triggerChange(),
        );
      },
    });
  };

  onDragStart = (event) => {
    event.stopPropagation();

    if (!this.props.multiple || this.props.disabled) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    const dragSourceId = event.currentTarget.dataset.fileid;

    this.setState({ dragSourceId });
  };

  onDragEnd = (event) => {
    event.stopPropagation();

    if (this.props.disabled) {
      return;
    }

    this.setState({ dragSourceId: '' });
  };

  onDragEnter = (event) => {
    event.stopPropagation();

    if (this.props.disabled) {
      return;
    }

    const dragTargetId = event.currentTarget.dataset.fileid;

    if (this.state.dragSourceId === dragTargetId) {
      return;
    }

    this.setSortOrder(this.state.dragSourceId, dragTargetId);
  };

  onDragLeave = (event) => {
    event.stopPropagation();
  };

  setSortOrder(sourceId, targetId) {
    const sortOrder = this.props.value.map((file) => this.fileId(file));
    const sourceIndex = findIndex(
      this.props.value,
      (file) => this.fileId(file) === sourceId,
    );
    const targetIndex = findIndex(
      this.props.value,
      (file) => this.fileId(file) === targetId,
    );
    sortOrder.splice(targetIndex, 0, sortOrder.splice(sourceIndex, 1)[0]);
    this.setState({ sortOrder }, () => this.triggerChange());
  }

  render() {
    const {
      value,
      label,
      typeLabel = '',
      valuePath,
      help,
      multiple = false,
      minimal = false,
      wide = false,
      fullSize = true,
      sizeType,
      maxWidth,
      maxHeight,
      minWidth,
      minHeight,
      accept,
      readonlyContent,
      readonly,
      ...props
    } = this.props;

    const { uploading } = this.state;

    let values;
    if (!multiple) {
      values = value ? [value] : [];
    } else {
      values = value || [];
    }

    const noSize =
      (maxWidth || maxHeight || minWidth || minHeight) !== undefined;

    const files = Array.isArray(values)
      ? values.reduce((acc, file) => {
          const fileVal = valuePath ? get(file, valuePath) : file;

          if (fileVal) {
            acc.push({
              id: fileVal.id,
              url: fileVal.url,
              filename: fileVal.filename,
              file: fileVal,
            });
          }

          return acc;
        }, [])
      : [];

    const hasFiles = files.length > 0;

    return (
      <div
        className={classNames('form-file', {
          single: !multiple,
          minimal: Boolean(minimal),
          empty: !hasFiles,
        })}
      >
        <Label label={label} help={help} htmlFor={props.id} />

        <div
          className={classNames('form-field-input', {
            wide: Boolean(wide),
            uploading: Boolean(uploading),
            dragging: Boolean(this.state.dragSourceId),
          })}
          style={{
            minWidth: minWidth || 0,
            minHeight: minHeight || (hasFiles ? 0 : undefined),
          }}
        >
          <div className="form-file-emptystate">
            <FadeIn active={!hasFiles} transitionAppear={false}>
              <FileDropzone
                className="form-file-dropzone"
                acceptClassName="form-file-dropzone-accepted"
                rejectClassName="form-file-dropzone-rejected"
                accept={accept || ''}
                onDrop={this.onUploadFiles}
                multiple={multiple}
                disabled={props.disabled}
              >
                <div className="form-file-upload">
                  <div className="as-link">
                    Add {typeLabel ? typeLabel : <>file{multiple ? 's' : ''}</>}
                  </div>
                  &emsp;
                  <div className="muted">Drag & drop to upload</div>
                </div>
              </FileDropzone>
            </FadeIn>
          </div>

          {hasFiles && (
            <ul className="form-file-list">
              <CSSTransitionGroup>
                {files.map((file, index) => (
                  <FadeIn key={!multiple ? 0 : file.id} active={true}>
                    <li
                      className={classNames({
                        dragging: this.state.dragSourceId === file.id,
                        default: index === 0,
                        nosize: Boolean(noSize),
                      })}
                      data-index={index}
                      style={
                        fullSize
                          ? {
                              maxWidth,
                              maxHeight,
                              minWidth: minWidth
                                ? minWidth - 12
                                : 'calc(100% - 11px)',
                              minHeight: minHeight
                                ? minHeight - 12
                                : fullSize
                                ? 100
                                : undefined,
                            }
                          : undefined
                      }
                    >
                      <Droppable
                        types={droppableTypes}
                        data-fileid={file.id}
                        onDragEnter={this.onDragEnter}
                        onDragLeave={this.onDragLeave}
                      >
                        <Draggable
                          type="file"
                          data-fileid={file.id}
                          onDragStart={this.onDragStart}
                          onDragEnd={this.onDragEnd}
                          className={multiple ? 'draggable' : ''}
                        >
                          <div className="form-file-image">
                            <span>{file.filename || file.id}</span>
                          </div>

                          <div className="form-file-controls">
                            {!props.disabled && (
                              <button
                                data-fileid={file.id}
                                className="as-link"
                                onClick={this.onClickRemove}
                                type="button"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            )}

                            <a
                              href={file.url}
                              target="_blank"
                              data-id={file.id}
                              rel="noreferrer"
                            >
                              <i className="fa fa-file" />
                            </a>
                          </div>
                        </Draggable>
                      </Droppable>

                      <span className="form-file-mask" />
                    </li>
                  </FadeIn>
                ))}
              </CSSTransitionGroup>
            </ul>
          )}

          <FadeIn active={hasFiles}>
            <FileReader
              accept={accept || ''}
              onFiles={this.onUploadFiles}
              multiple={multiple}
              disabled={props.disabled}
            >
              {!props.disabled && (
                <button className="button button-secondary button-sm form-file-addlink">
                  {!multiple ? 'Replace file' : 'Add files'}
                </button>
              )}
            </FileReader>
          </FadeIn>

          <input
            {...props}
            ref={this.inputRef}
            type="hidden"
            name={props.name}
            disabled={props.disabled}
            defaultValue={undefined}
            value=""
          />
        </div>
      </div>
    );
  }
}
